import { useCallback } from 'react';

import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { isInTemplateRoute } from 'ui/router';

import { clearFormParam, setFormParam } from './form-url-params';
import { useEditJourneyFormOverlay } from './journey-form/edit-journey-form-overlay';
import { useEmailTriggerSidebar } from './sidebars/email-trigger-sidebar';
import {
  showTemplatePollingSidebar,
  useScheduledPollingSidebar,
} from './sidebars/scheduled-polling';
import { showTemplateWebhookSidebar, useWebhookSidebar } from './sidebars/webhook-sidebar';

type OpenTriggerOptions = {
  onHide?: () => void;
};

export const useOpenTrigger = () => {
  const { journeyForms, webhooks, emailTriggers, pollingConfigs } = useJourneyContext();
  const showWebhookSidebar = useWebhookSidebar();
  const showEmailTriggerSidebar = useEmailTriggerSidebar();
  const showPollingSidebar = useScheduledPollingSidebar();
  const showEditFormOverlay = useEditJourneyFormOverlay();

  return useCallback(
    (triggerShortId: string, options: OpenTriggerOptions = {}): boolean => {
      const isTemplate = isInTemplateRoute();

      const form = journeyForms.find((item) => item.shortId === triggerShortId);
      if (form) {
        setFormParam(form.shortId);
        showEditFormOverlay(
          { journeyId: form.journey, formId: form.shortId },
          {
            onHide: () => {
              clearFormParam();
              options.onHide?.();
            },
          },
        );
        return true;
      }

      const webhook = webhooks.find((item) => item.shortId === triggerShortId);
      if (webhook) {
        if (isTemplate) {
          showTemplateWebhookSidebar({ webhookShortId: webhook.shortId });
        } else {
          showWebhookSidebar({ webhookShortId: webhook.shortId }, { onHide: options.onHide });
        }
        return true;
      }

      const emailTrigger = emailTriggers.find((item) => item.shortId === triggerShortId);
      if (emailTrigger) {
        showEmailTriggerSidebar(
          { triggerShortId: emailTrigger.shortId },
          { onHide: options.onHide },
        );
        return true;
      }

      const pollingConfig = pollingConfigs.find((item) => item.shortId === triggerShortId);
      if (pollingConfig) {
        const props = {
          journeyShortId: pollingConfig.journeyShortId,
          pollingConfigShortId: pollingConfig.shortId,
        };
        if (isTemplate) {
          showTemplatePollingSidebar(props);
        } else {
          showPollingSidebar(props, { onHide: options.onHide });
        }
        return true;
      }

      return false;
    },
    [
      journeyForms,
      webhooks,
      emailTriggers,
      pollingConfigs,
      showWebhookSidebar,
      showEmailTriggerSidebar,
      showPollingSidebar,
      showEditFormOverlay,
    ],
  );
};
